import { useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import type { Item } from '../../lib/supabase'
import { needQuantity, type Need } from '../../lib/recipeBook'
import { initialChoices, matchFor, mergeStatus, resultQuantity, type Choice } from '../../lib/choices'
import { userLocale } from '../../lib/locale'
import { CheckIcon } from '../icons'
import { knownNames, nameSuggestions } from '../../lib/catalog'
import { indexItems } from '../../lib/search'
import { NameField } from './NameField'

// What "Add to list" will do, decided item by item (feature spec 02). Items
// the list already has — open or in the basket — must be decided: Keep mine,
// Use recipe, or Add both (not for the basket: what was bought last time isn't
// what's needed now), or Not the same when the match is wrong. New items start
// ticked; pantry staples start unticked. The decisions are lib/choices.ts.
//
// Every match is said out loud: a row named after the household's own
// spelling says which word the recipe used ("Use “…”" takes it back), and an
// item matched on the list says what it was matched to. Tapping a name edits
// the row — name and amount, with the household's names suggested as you type
// — and the row is matched again under its new name.

const NEEDS_LABEL = { recipe: 'Recipe needs', recipes: 'Recipes need', plan: 'Plan needs' } as const

const sameText = (a: string, b: string) => a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase()

export function ChoicesPanel({ needs, items, before = [], source, locale = userLocale(), onApply }: {
  needs: Need[]
  items: Item[]
  /** Names bought before and not on the list (lib/catalog.ts), suggested when a row is edited. */
  before?: { name: string }[]
  source: 'recipe' | 'recipes' | 'plan'
  locale?: string
  /** The rows as edited here, and what to do with each. */
  onApply: (needs: Need[], choices: Choice[]) => void
}) {
  const [rows, setRows] = useState<Need[]>(needs)
  // Matched when the sheet opens and when a row is renamed — not when another
  // phone changes the list meanwhile, so a card doesn't jump away.
  const matches = useMemo(() => rows.map(n => matchFor(n, items)), [rows]) // eslint-disable-line react-hooks/exhaustive-deps
  const [choices, setChoices] = useState<Choice[]>(() => initialChoices(needs, items))
  const [editing, setEditing] = useState<number | null>(null)
  const known = useMemo(() => knownNames(items, before), [items, before])
  const knownIndex = useMemo(() => indexItems(known), [known])
  const status = mergeStatus(choices)
  const conflictIdx = rows.map((_, k) => k).filter(k => matches[k])
  const newIdx = rows.map((_, k) => k).filter(k => !matches[k])
  const ticked = newIdx.filter(k => choices[k] === 'add').length
  const undecided = conflictIdx.filter(k => choices[k] === null).length

  const set = (k: number, c: Choice) => setChoices(cs => cs.map((x, i) => (i === k ? c : x)))
  const setAll = (ks: number[], c: Choice) => setChoices(cs => cs.map((x, i) => (ks.includes(i) ? c : x)))

  /** A row under a new name or amount: matched again, and its choice starts over if the match changed. */
  function change(k: number, patch: Pick<Need, 'name'> & Partial<Pick<Need, 'quantityText'>>) {
    const row = { ...rows[k], ...patch }
    const renamed = !sameText(row.name, rows[k].name)
    setRows(rs => rs.map((r, i) => (i === k ? row : r)))
    if (renamed) set(k, initialChoices([row], items)[0])
    setEditing(null)
  }
  const editor = (k: number) => (
    <RowEditor need={rows[k]} known={known} locale={locale}
      onSave={(name, quantityText) => change(k, { name, quantityText })} onCancel={() => setEditing(null)} />
  )
  const matchNote = (k: number) => (
    <MatchNote need={rows[k]} onUseOriginal={() => change(k, { name: rows[k].original })} />
  )
  // Close names that weren't sure enough to use unasked (lib/catalog.ts), to tap.
  const maybe = (k: number) => {
    const names = nameSuggestions(rows[k].name, knownIndex, 2, locale)
    if (!names.length) return null
    return (
      <p className="ml-choices__match">
        <span>Maybe the same as</span>
        {names.map(n => (
          <button key={n} type="button" className="ml-text-btn" aria-label={`Use ${n} for ${rows[k].name}`}
            onClick={() => change(k, { name: n })}>{n}</button>
        ))}
      </p>
    )
  }

  return (
    <div className="ml-choices">
      <div className="ml-choices__columns">
        {conflictIdx.length > 0 && (
          <section className="ml-choices__group" aria-label="Already on your list">
            <div className="ml-choices__head">
              <h3 className="ml-label">Already on your list</h3>
              <span className={`ml-choices__badge${undecided ? ' is-warn' : ''}`}>
                {undecided ? `${undecided} to decide` : 'All decided'}
              </span>
            </div>
            <p className="ml-choices__intro">You already have these. Pick what to do with each one.</p>
            <div className="ml-choices__bulk">
              <button type="button" className="ml-btn ml-btn--secondary ml-btn--row" onClick={() => setAll(conflictIdx, 'keep')}>Keep all mine</button>
              <button type="button" className="ml-btn ml-btn--secondary ml-btn--row" onClick={() => setAll(conflictIdx, 'recipe')}>Use recipe for all</button>
            </div>
            {conflictIdx.map(k => editing === k
              ? <div key={rows[k].key} className="ml-conflict">{editor(k)}</div>
              : (
                <ConflictCard key={rows[k].key} need={rows[k]} item={matches[k]!} choice={choices[k]}
                  needsLabel={NEEDS_LABEL[source]} locale={locale} onChoose={c => set(k, c)}
                  onEdit={() => setEditing(k)} matchNote={matchNote(k)} />
              ))}
          </section>
        )}

        {newIdx.length > 0 && (
          <section className="ml-choices__group" aria-label="New to your list">
            <div className="ml-choices__head">
              <h3 className="ml-label">New to your list</h3>
              <span className="ml-choices__badge">{ticked} selected</span>
              <button type="button" className="ml-text-btn ml-choices__all"
                onClick={() => setAll(newIdx, ticked === newIdx.length ? 'skip' : 'add')}>
                {ticked === newIdx.length ? 'Select none' : 'Select all'}
              </button>
            </div>
            <ul className="ml-choices__new">
              {newIdx.map(k => {
                const on = choices[k] === 'add'
                const row = rows[k]
                if (editing === k) return <li key={row.key} className="ml-choices__item is-editing">{editor(k)}</li>
                return (
                  <li key={row.key} className="ml-choices__item">
                    <button type="button" role="checkbox" aria-checked={on} aria-label={row.name} className="ml-choices__check"
                      onClick={() => set(k, on ? 'skip' : 'add')}>
                      <span className={`ml-choices__tick${on ? ' is-on' : ''}`} aria-hidden="true">{on && <CheckIcon size={14} />}</span>
                    </button>
                    <div className="ml-choices__body">
                      <button type="button" className="ml-choices__edit" aria-label={`Edit ${row.name}`} onClick={() => setEditing(k)}>
                        <span className="ml-choices__name">{row.name}</span>
                        <span className="ml-choices__qty ml-num">{needQuantity(row, locale)}</span>
                      </button>
                      {matchNote(k)}
                      {maybe(k)}
                    </div>
                  </li>
                )
              })}
            </ul>
          </section>
        )}
      </div>

      <p className="ml-choices__summary">{status.summary}</p>
      <button type="button" className="ml-btn ml-btn--primary ml-btn--wide ml-choices__apply"
        aria-disabled={status.canApply ? undefined : 'true'}
        onClick={() => { if (status.canApply) onApply(rows, choices) }}>
        {status.buttonLabel}
      </button>
    </div>
  )
}

function ConflictCard({ need, item, choice, needsLabel, locale, onChoose, onEdit, matchNote }: {
  need: Need
  item: Item
  choice: Choice
  needsLabel: string
  locale: string
  onChoose: (c: Choice) => void
  onEdit: () => void
  matchNote: ReactNode
}) {
  const options: { value: Choice; label: string }[] = [
    { value: 'keep', label: 'Keep mine' },
    { value: 'recipe', label: 'Use recipe' },
    ...(item.done ? [] : [{ value: 'combine' as Choice, label: 'Add both' }]),
  ]
  // A radio group: one Tab stop (the chosen option, else the first), and the
  // arrow keys move and choose, wrapping at the ends.
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  const focusable = Math.max(0, options.findIndex(o => o.value === choice))
  function onKey(e: KeyboardEvent, i: number) {
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0
    if (!step) return
    e.preventDefault()
    const next = (i + step + options.length) % options.length
    onChoose(options[next].value)
    refs.current[next]?.focus()
  }
  const named = (q: string | null) => (q ? `${item.name}, ${q}` : item.name)
  const outcome = choice === null ? 'Pick one to continue'
    : choice === 'separate' ? 'Added as a new item'
    : choice === 'keep' ? (item.done ? `Stays: ${item.name}, in the basket` : `Stays: ${named(item.quantity)}`)
    : `Becomes: ${named(resultQuantity(item, need, choice, locale))}`

  return (
    <div className={`ml-conflict${choice === null ? ' is-open' : ''}`} role="group" aria-label={need.name}>
      <div className="ml-conflict__top">
        <button type="button" className="ml-conflict__name" aria-label={`Edit ${need.name}`} onClick={onEdit}>{need.name}</button>
        <span className="ml-label ml-conflict__state">{choice === null ? 'Pick one' : 'Done'}</span>
      </div>
      {!sameText(item.name, need.name) && (
        <p className="ml-choices__match">Matched to <strong>{item.name}</strong> on your list</p>
      )}
      {matchNote}
      <div className="ml-conflict__amounts">
        <div className="ml-conflict__mine">
          <span className="ml-label">On your list</span>
          <span className="ml-num">{item.done ? 'In the basket' : item.quantity || 'No amount'}</span>
        </div>
        <div className="ml-conflict__theirs">
          <span className="ml-label">{needsLabel}</span>
          <span className="ml-num">{needQuantity(need, locale) || 'No amount'}</span>
        </div>
      </div>
      <div className="ml-conflict__options" role="radiogroup" aria-label={`What to do with ${need.name}`}>
        {options.map((o, i) => (
          <button key={o.value} ref={el => { refs.current[i] = el }} type="button" role="radio" aria-checked={choice === o.value}
            tabIndex={i === focusable ? 0 : -1}
            className={`ml-conflict__option${choice === o.value ? ' is-selected' : ''}`}
            onClick={() => onChoose(o.value)} onKeyDown={e => onKey(e, i)}>
            {o.label}
          </button>
        ))}
      </div>
      <div className="ml-conflict__foot">
        <span className={choice === null ? 'ml-conflict__outcome is-warn' : 'ml-conflict__outcome'} aria-live="polite">{outcome}</span>
        <button type="button" className="ml-text-btn ml-text-btn--quiet"
          onClick={() => onChoose(choice === 'separate' ? null : 'separate')}>
          {choice === 'separate' ? 'It is the same' : 'Not the same'}
        </button>
      </div>
    </div>
  )
}

/** "Your name for “fast potatis”" with the way back, when the row isn't called what the recipe said. */
function MatchNote({ need, onUseOriginal }: { need: Need; onUseOriginal: () => void }) {
  if (sameText(need.name, need.original)) return null
  return (
    <p className="ml-choices__match">
      <span>Recipe says <strong>{need.original}</strong></span>
      <button type="button" className="ml-text-btn ml-text-btn--quiet" onClick={onUseOriginal}
        aria-label={`Use the recipe's name, ${need.original}`}>
        Use “{need.original}”
      </button>
    </p>
  )
}

/** Name and amount of one row, with the household's names suggested as you type. */
function RowEditor({ need, known, locale, onSave, onCancel }: {
  need: Need
  known: ReturnType<typeof knownNames>
  locale: string
  onSave: (name: string, quantityText: string | undefined) => void
  onCancel: () => void
}) {
  const [name, setName] = useState(need.name)
  const shown = needQuantity(need, locale)
  const [amount, setAmount] = useState(shown)
  const save = () => {
    if (!name.trim()) return
    // An untouched amount stays the recipe's, so it can still be added up.
    onSave(name.trim().replace(/\s+/g, ' '), amount === shown ? need.quantityText : amount)
  }
  return (
    <form className="ml-choices__editor" aria-label={`Edit ${need.name}`}
      onSubmit={e => { e.preventDefault(); save() }}
      onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); onCancel() } }}>
      <NameField label="Name" value={name} known={known} onChange={setName} autoFocus />
      <label className="ml-field">
        <span className="ml-label">Amount</span>
        <input className="ml-input" value={amount} onChange={e => setAmount(e.target.value)} autoComplete="off" />
      </label>
      <div className="ml-choices__editor-actions">
        <button type="button" className="ml-btn ml-btn--secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="ml-btn ml-btn--primary" aria-disabled={name.trim() ? undefined : 'true'}>Save</button>
      </div>
    </form>
  )
}
